// Collision model: box traces through the BSP tree, brushes and patches.
// A port of Wolfenstein: Enemy Territory qcommon/cm_load.c + cm_trace.c,
// including its MRE_OPTIMIZE bounds handling and CM_TransformedBoxTrace for
// moved / rotated inline models (capsule traces are not ported).
// Arithmetic follows the C float types (see f32.ts); SURFACE_CLIP_EPSILON is a
// double literal in the original, so expressions using it are evaluated in double.

import { SurfaceType, type Bsp } from '../bsp';
import { M_PI_F, f, fangleVectors, fdot } from '../f32';
import type { Vec3 } from '../math';
import { generatePatchCollide, positionTestInPatchCollide, traceThroughPatchCollide } from './patch';
import { makePlane, type CBrush, type CLeaf, type CModel, type CNode, type CPatch, type CPlane, type TraceResult } from './types';

export const SURFACE_CLIP_EPSILON = 0.125;

interface TraceWork {
  start: Vec3;
  end: Vec3;
  dir: Vec3;
  /** size[0] = mins, size[1] = maxs, both symmetric around 0. */
  size: [Vec3, Vec3];
  offsets: Vec3[];
  extents: Vec3;
  maxOffset: number;
  bounds: [Vec3, Vec3];
  isPoint: boolean;
  contents: number;
  trace: TraceResult;
}

export function emptyTrace(): TraceResult {
  return {
    allsolid: false,
    startsolid: false,
    fraction: 1,
    endpos: [0, 0, 0],
    plane: { normal: [0, 0, 0], dist: 0 },
    surfaceFlags: 0,
    contents: 0,
    entityNum: -1,
  };
}

/** CM_CalcTraceBounds: swept box bounds up to the current fraction. */
function calcTraceBounds(tw: TraceWork, expand: boolean): void {
  const frac = tw.trace.fraction;
  for (let i = 0; i < 3; i++) {
    if (tw.start[i] < tw.end[i]) {
      tw.bounds[0][i] = f(tw.start[i] + tw.size[0][i]);
      tw.bounds[1][i] = f(f(tw.start[i] + f(frac * tw.dir[i])) + tw.size[1][i]);
    } else {
      tw.bounds[0][i] = f(f(tw.start[i] + f(frac * tw.dir[i])) + tw.size[0][i]);
      tw.bounds[1][i] = f(tw.start[i] + tw.size[1][i]);
    }
    if (expand) {
      // expand for epsilon
      tw.bounds[0][i] = f(tw.bounds[0][i] - 1);
      tw.bounds[1][i] = f(tw.bounds[1][i] + 1);
    }
  }
}

const boundsOverlap = (tw: TraceWork, mins: Vec3, maxs: Vec3): boolean =>
  !(
    mins[0] > tw.bounds[1][0] ||
    mins[1] > tw.bounds[1][1] ||
    mins[2] > tw.bounds[1][2] ||
    maxs[0] < tw.bounds[0][0] ||
    maxs[1] < tw.bounds[0][1] ||
    maxs[2] < tw.bounds[0][2]
  );

export class CollisionModel {
  planes: CPlane[] = [];
  nodes: CNode[] = [];
  leafs: CLeaf[] = [];
  brushes: CBrush[] = [];
  patches: CPatch[] = [];
  models: CModel[] = [];
  private checkcount = 0;

  static fromBsp(bsp: Bsp): CollisionModel {
    const cm = new CollisionModel();
    cm.planes = bsp.planes.map((p) => makePlane([p.normal[0], p.normal[1], p.normal[2]], p.dist));
    cm.nodes = bsp.nodes.map((n) => ({ plane: cm.planes[n.planeNum], children: [n.children[0], n.children[1]] }));

    cm.brushes = bsp.brushes.map((b) => {
      const sides = [];
      for (let i = 0; i < b.numSides; i++) {
        const s = bsp.brushSides[b.firstSide + i];
        sides.push({ plane: cm.planes[s.planeNum], surfaceFlags: bsp.shaders[s.shaderNum]?.surfaceFlags ?? 0 });
      }
      const brush: CBrush = {
        contents: bsp.shaders[b.shaderNum]?.contentFlags ?? 0,
        sides,
        mins: [-Infinity, -Infinity, -Infinity],
        maxs: [Infinity, Infinity, Infinity],
        checkcount: 0,
      };
      // CM_BoundBrush: q3map always writes the six axial planes first (-x +x -y +y -z +z)
      if (sides.length >= 6) {
        brush.mins = [-sides[0].plane.dist, -sides[2].plane.dist, -sides[4].plane.dist];
        brush.maxs = [sides[1].plane.dist, sides[3].plane.dist, sides[5].plane.dist];
      }
      return brush;
    });

    // CMod_LoadPatches: patches indexed by surface number
    const patchIndexBySurface = new Map<number, number>();
    bsp.surfaces.forEach((surf, surfNum) => {
      if (surf.surfaceType !== SurfaceType.Patch) return;
      const shader = bsp.shaders[surf.shaderNum];
      const pts: Vec3[] = [];
      const { xyz } = bsp.verts;
      for (let i = 0; i < surf.patchWidth * surf.patchHeight; i++) {
        const v = surf.firstVert + i;
        pts.push([xyz[v * 3], xyz[v * 3 + 1], xyz[v * 3 + 2]]);
      }
      const pc = generatePatchCollide(surf.patchWidth, surf.patchHeight, pts);
      if (!pc) return;
      patchIndexBySurface.set(surfNum, cm.patches.length);
      cm.patches.push({ surface: surfNum, contents: shader?.contentFlags ?? 0, surfaceFlags: shader?.surfaceFlags ?? 0, pc, checkcount: 0 });
    });

    cm.leafs = bsp.leafs.map((l) => {
      const brushes: number[] = [];
      for (let i = 0; i < l.numLeafBrushes; i++) brushes.push(bsp.leafBrushes[l.firstLeafBrush + i]);
      const patches: number[] = [];
      for (let i = 0; i < l.numLeafSurfaces; i++) {
        const p = patchIndexBySurface.get(bsp.leafSurfaces[l.firstLeafSurface + i]);
        if (p !== undefined) patches.push(p);
      }
      return { brushes, patches };
    });
    if (!cm.leafs.length) cm.leafs.push({ brushes: [], patches: [] });

    cm.models = bsp.models.map((m, i) => {
      // CMod_LoadSubmodels: a "leaf" holding the model's brushes and surfaces (patches);
      // the world model's leaf is never used
      const brushes: number[] = [];
      const patches: number[] = [];
      if (i > 0) {
        for (let k = 0; k < m.numBrushes; k++) brushes.push(m.firstBrush + k);
        for (let k = 0; k < m.numSurfaces; k++) {
          const p = patchIndexBySurface.get(m.firstSurface + k);
          if (p !== undefined) patches.push(p);
        }
      }
      return {
        // spread the mins / maxs by a pixel
        mins: [m.mins[0] - 1, m.mins[1] - 1, m.mins[2] - 1],
        maxs: [m.maxs[0] + 1, m.maxs[1] + 1, m.maxs[2] + 1],
        leaf: { brushes, patches },
      };
    });
    return cm;
  }

  /** Root of the world BSP tree (negative numbers are leafs). */
  get headnode(): number {
    return this.nodes.length ? 0 : -1;
  }

  /**
   * CM_BoxTrace for the world, CM_TransformedBoxTrace for inline models.
   * @param model 0 = world (BSP tree), >0 = inline model (brush entity)
   * @param origin position of the inline model
   * @param angles rotation of the inline model (pitch yaw roll, degrees)
   */
  boxTrace(start: Vec3, end: Vec3, mins: Vec3, maxs: Vec3, mask: number, model = 0, origin?: Vec3, angles?: Vec3): TraceResult {
    if (!model && !origin) return this.trace(start, end, mins, maxs, mask, 0);
    // adjust so that mins and maxs are always symmetric, which avoids some
    // complications with plane expanding of rotated bmodels
    const o = origin ?? [0, 0, 0];
    const sym0: Vec3 = [0, 0, 0];
    const sym1: Vec3 = [0, 0, 0];
    let startL: Vec3 = [0, 0, 0];
    let endL: Vec3 = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
      const offset = f(f(mins[i] + maxs[i]) * 0.5);
      sym0[i] = f(mins[i] - offset);
      sym1[i] = f(maxs[i] - offset);
      // subtract origin offset
      startL[i] = f(f(start[i] + offset) - o[i]);
      endL[i] = f(f(end[i] + offset) - o[i]);
    }
    // rotate start and end into the model's frame of reference
    const rotated = !!angles && (angles[0] !== 0 || angles[1] !== 0 || angles[2] !== 0);
    let matrix: Vec3[] = [];
    if (rotated) {
      matrix = createRotationMatrix(angles);
      startL = rotatePoint(startL, matrix);
      endL = rotatePoint(endL, matrix);
    }
    const trace = this.trace(startL, endL, sym0, sym1, mask, model);
    // rotation of the bmodel collision plane back into the world
    if (rotated && trace.fraction !== 1) {
      const transpose = [0, 1, 2].map((r) => [matrix[0][r], matrix[1][r], matrix[2][r]] as Vec3);
      trace.plane.normal = rotatePoint(trace.plane.normal, transpose);
    }
    // the end position from the original (unrotated, untranslated) points
    for (let i = 0; i < 3; i++) trace.endpos[i] = f(start[i] + f(trace.fraction * f(end[i] - start[i])));
    return trace;
  }

  /** CM_Trace */
  private trace(start: Vec3, end: Vec3, mins: Vec3, maxs: Vec3, mask: number, model: number): TraceResult {
    this.checkcount++;
    const trace = emptyTrace();
    const startL = start;
    const endL = end;

    // adjust so that mins and maxs are always symmetric
    const offset: Vec3 = [0, 0, 0];
    const size0: Vec3 = [0, 0, 0];
    const size1: Vec3 = [0, 0, 0];
    const tws: Vec3 = [0, 0, 0];
    const twe: Vec3 = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
      offset[i] = f(f(mins[i] + maxs[i]) * 0.5);
      size0[i] = f(mins[i] - offset[i]);
      size1[i] = f(maxs[i] - offset[i]);
      tws[i] = f(startL[i] + offset[i]);
      twe[i] = f(endL[i] + offset[i]);
    }
    const isPoint = size0[0] === 0 && size0[1] === 0 && size0[2] === 0;
    const tw: TraceWork = {
      start: tws,
      end: twe,
      dir: [f(twe[0] - tws[0]), f(twe[1] - tws[1]), f(twe[2] - tws[2])],
      size: [size0, size1],
      // offsets[signbits] = vector to the appropriate corner from origin
      offsets: [
        [size0[0], size0[1], size0[2]],
        [size1[0], size0[1], size0[2]],
        [size0[0], size1[1], size0[2]],
        [size1[0], size1[1], size0[2]],
        [size0[0], size0[1], size1[2]],
        [size1[0], size0[1], size1[2]],
        [size0[0], size1[1], size1[2]],
        [size1[0], size1[1], size1[2]],
      ],
      extents: isPoint ? [0, 0, 0] : [size1[0], size1[1], size1[2]],
      maxOffset: f(f(size1[0] + size1[1]) + size1[2]),
      bounds: [
        [0, 0, 0],
        [0, 0, 0],
      ],
      isPoint,
      contents: mask,
      trace,
    };

    const positionTest = start[0] === end[0] && start[1] === end[1] && start[2] === end[2];
    calcTraceBounds(tw, !positionTest);

    if (positionTest) {
      if (model) {
        const cmod = this.models[model];
        if (cmod) this.testInLeaf(tw, cmod.leaf);
      } else {
        this.positionTest(tw);
      }
    } else if (model) {
      const cmod = this.models[model];
      if (cmod) this.traceThroughLeaf(tw, cmod.leaf);
    } else {
      this.traceThroughTree(tw, this.headnode, 0, 1, tw.start, tw.end);
    }

    // generate endpos from the original, unmodified start/end
    if (trace.fraction === 1) {
      trace.endpos = [end[0], end[1], end[2]];
    } else {
      for (let i = 0; i < 3; i++) trace.endpos[i] = f(start[i] + f(trace.fraction * f(end[i] - start[i])));
    }
    return trace;
  }

  // --- position testing (start == end) ---

  private positionTest(tw: TraceWork): void {
    // identify the leafs we are touching
    const lb: [Vec3, Vec3] = [
      [f(f(tw.start[0] + tw.size[0][0]) - 1), f(f(tw.start[1] + tw.size[0][1]) - 1), f(f(tw.start[2] + tw.size[0][2]) - 1)],
      [f(f(tw.start[0] + tw.size[1][0]) + 1), f(f(tw.start[1] + tw.size[1][1]) + 1), f(f(tw.start[2] + tw.size[1][2]) + 1)],
    ];
    const leafs: number[] = [];
    this.boxLeafnums(lb, this.headnode, leafs);
    this.checkcount++;
    for (const l of leafs) {
      this.testInLeaf(tw, this.leafs[l]);
      if (tw.trace.allsolid) break;
    }
  }

  /** CM_BoxLeafnums_r */
  private boxLeafnums(b: [Vec3, Vec3], nodenum: number, out: number[]): void {
    for (;;) {
      if (nodenum < 0) {
        out.push(-1 - nodenum);
        return;
      }
      const node = this.nodes[nodenum];
      const s = boxOnPlaneSide(b[0], b[1], node.plane);
      if (s === 1) nodenum = node.children[0];
      else if (s === 2) nodenum = node.children[1];
      else {
        // go down both
        this.boxLeafnums(b, node.children[0], out);
        nodenum = node.children[1];
      }
    }
  }

  private testInLeaf(tw: TraceWork, leaf: CLeaf): void {
    // test box position against all brushes in the leaf
    for (const k of leaf.brushes) {
      const b = this.brushes[k];
      if (b.checkcount === this.checkcount) continue; // already checked this brush in another leaf
      b.checkcount = this.checkcount;
      if (!(b.contents & tw.contents)) continue;
      this.testBoxInBrush(tw, b);
      if (tw.trace.allsolid) return;
    }
    // test against all patches
    for (const k of leaf.patches) {
      const p = this.patches[k];
      if (p.checkcount === this.checkcount) continue;
      p.checkcount = this.checkcount;
      if (!(p.contents & tw.contents)) continue;
      if (positionTestInPatchCollide(tw, p.pc)) {
        tw.trace.startsolid = tw.trace.allsolid = true;
        tw.trace.fraction = 0;
        return;
      }
    }
  }

  private testBoxInBrush(tw: TraceWork, brush: CBrush): void {
    if (!brush.sides.length) return;
    // special test for axial: the first 6 brush planes are always axial
    if (!boundsOverlap(tw, brush.mins, brush.maxs)) return;
    for (let i = 6; i < brush.sides.length; i++) {
      const plane = brush.sides[i].plane;
      // adjust the plane distance appropriately for mins/maxs
      const dist = f(plane.dist - fdot(tw.offsets[plane.signbits], plane.normal));
      const d1 = f(fdot(tw.start, plane.normal) - dist);
      // if completely in front of face, no intersection
      if (d1 > 0) return;
    }
    // inside this brush
    tw.trace.startsolid = tw.trace.allsolid = true;
    tw.trace.fraction = 0;
    tw.trace.contents = brush.contents;
  }

  // --- sweeping ---

  private traceThroughTree(tw: TraceWork, num: number, p1f: number, p2f: number, p1: Vec3, p2: Vec3): void {
    if (tw.trace.fraction <= p1f) return; // already hit something nearer

    if (num < 0) {
      this.traceThroughLeaf(tw, this.leafs[-1 - num]);
      return;
    }

    const node = this.nodes[num];
    const plane = node.plane;
    let t1: number;
    let t2: number;
    let offset: number;
    if (plane.type < 3) {
      t1 = f(p1[plane.type] - plane.dist);
      t2 = f(p2[plane.type] - plane.dist);
      offset = tw.extents[plane.type];
    } else {
      t1 = f(fdot(plane.normal, p1) - plane.dist);
      t2 = f(fdot(plane.normal, p2) - plane.dist);
      offset = tw.isPoint ? 0 : tw.maxOffset;
    }

    // see which sides we need to consider
    if (t1 >= offset + 1 && t2 >= offset + 1) {
      this.traceThroughTree(tw, node.children[0], p1f, p2f, p1, p2);
      return;
    }
    if (t1 < -offset - 1 && t2 < -offset - 1) {
      this.traceThroughTree(tw, node.children[1], p1f, p2f, p1, p2);
      return;
    }

    // put the crosspoint SURFACE_CLIP_EPSILON pixels on the near side
    let side: number;
    let frac: number;
    let frac2: number;
    if (t1 < t2) {
      const idist = f(1.0 / f(t1 - t2));
      side = 1;
      frac2 = f((f(t1 + offset) + SURFACE_CLIP_EPSILON) * idist);
      frac = f((f(t1 - offset) + SURFACE_CLIP_EPSILON) * idist);
    } else if (t1 > t2) {
      const idist = f(1.0 / f(t1 - t2));
      side = 0;
      frac2 = f((f(t1 - offset) - SURFACE_CLIP_EPSILON) * idist);
      frac = f((f(t1 + offset) + SURFACE_CLIP_EPSILON) * idist);
    } else {
      side = 0;
      frac = 1;
      frac2 = 0;
    }

    // move up to the node
    if (frac < 0) frac = 0;
    if (frac > 1) frac = 1;
    let midf = f(p1f + f(f(p2f - p1f) * frac));
    let mid: Vec3 = [f(p1[0] + f(frac * f(p2[0] - p1[0]))), f(p1[1] + f(frac * f(p2[1] - p1[1]))), f(p1[2] + f(frac * f(p2[2] - p1[2])))];
    this.traceThroughTree(tw, node.children[side], p1f, midf, p1, mid);

    // go past the node
    if (frac2 < 0) frac2 = 0;
    if (frac2 > 1) frac2 = 1;
    midf = f(p1f + f(f(p2f - p1f) * frac2));
    mid = [f(p1[0] + f(frac2 * f(p2[0] - p1[0]))), f(p1[1] + f(frac2 * f(p2[1] - p1[1]))), f(p1[2] + f(frac2 * f(p2[2] - p1[2])))];
    this.traceThroughTree(tw, node.children[side ^ 1], midf, p2f, mid, p2);
  }

  private traceThroughLeaf(tw: TraceWork, leaf: CLeaf): void {
    // trace line against all brushes in the leaf
    for (const k of leaf.brushes) {
      const b = this.brushes[k];
      if (b.checkcount === this.checkcount) continue; // already checked this brush in another leaf
      b.checkcount = this.checkcount;
      if (!(b.contents & tw.contents)) continue;
      if (!boundsOverlap(tw, b.mins, b.maxs)) continue;
      const fraction = tw.trace.fraction;
      this.traceThroughBrush(tw, b);
      if (!tw.trace.fraction) return;
      if (tw.trace.fraction < fraction) calcTraceBounds(tw, true);
    }
    // trace line against all patches in the leaf
    for (const k of leaf.patches) {
      const p = this.patches[k];
      if (p.checkcount === this.checkcount) continue; // already checked this patch in another leaf
      p.checkcount = this.checkcount;
      if (!(p.contents & tw.contents)) continue;
      if (!boundsOverlap(tw, p.pc.bounds[0], p.pc.bounds[1])) continue;
      const fraction = tw.trace.fraction;
      traceThroughPatchCollide(tw, p.pc);
      if (tw.trace.fraction < fraction) {
        tw.trace.surfaceFlags = p.surfaceFlags;
        tw.trace.contents = p.contents;
      }
      if (!tw.trace.fraction) return;
      if (tw.trace.fraction < fraction) calcTraceBounds(tw, true);
    }
  }

  private traceThroughBrush(tw: TraceWork, brush: CBrush): void {
    let enterFrac = -1;
    let leaveFrac = 1;
    let clipplane: CPlane | null = null;
    let leadSurfaceFlags = 0;
    let getout = false;
    let startout = false;
    const { start, end, offsets } = tw;

    // compare the trace against all planes of the brush: find the latest time the trace
    // crosses a plane towards the interior and the earliest time it crosses one towards the exterior
    for (const side of brush.sides) {
      const plane = side.plane;
      const n = plane.normal;
      // adjust the plane distance appropriately for mins/maxs
      const dist = f(plane.dist - fdot(offsets[plane.signbits], n));
      const d1 = f(fdot(start, n) - dist);
      const d2 = f(fdot(end, n) - dist);

      if (d2 > 0) getout = true; // endpoint is not in solid
      if (d1 > 0) startout = true;

      // if completely in front of face, no intersection with the entire brush
      if (d1 > 0 && (d2 >= SURFACE_CLIP_EPSILON || d2 >= d1)) return;
      // if it doesn't cross the plane, the plane isn't relevant
      if (d1 <= 0 && d2 <= 0) continue;

      // crosses face
      if (d1 > d2) {
        // enter
        let fr = f((d1 - SURFACE_CLIP_EPSILON) / f(d1 - d2));
        if (fr < 0) fr = 0;
        if (fr > enterFrac) {
          enterFrac = fr;
          clipplane = plane;
          leadSurfaceFlags = side.surfaceFlags;
        }
      } else {
        // leave
        let fr = f((d1 + SURFACE_CLIP_EPSILON) / f(d1 - d2));
        if (fr > 1) fr = 1;
        if (fr < leaveFrac) leaveFrac = fr;
      }
    }

    // all planes have been checked, and the trace was not completely outside the brush
    if (!startout) {
      // original point was inside brush
      tw.trace.startsolid = true;
      if (!getout) {
        tw.trace.allsolid = true;
        tw.trace.fraction = 0;
      }
      return;
    }

    if (enterFrac < leaveFrac && enterFrac > -1 && enterFrac < tw.trace.fraction && clipplane) {
      if (enterFrac < 0) enterFrac = 0;
      tw.trace.fraction = enterFrac;
      tw.trace.plane = { normal: [clipplane.normal[0], clipplane.normal[1], clipplane.normal[2]], dist: clipplane.dist };
      tw.trace.surfaceFlags = leadSurfaceFlags;
      tw.trace.contents = brush.contents;
    }
  }

  /** CM_TransformedPointContents: contents of all brushes containing the point. */
  pointContents(p: Vec3, model = 0, origin?: Vec3, angles?: Vec3): number {
    let lp: Vec3 = origin ? [f(p[0] - origin[0]), f(p[1] - origin[1]), f(p[2] - origin[2])] : p;
    if (model && angles && (angles[0] || angles[1] || angles[2])) {
      const forward: Vec3 = [0, 0, 0];
      const right: Vec3 = [0, 0, 0];
      const up: Vec3 = [0, 0, 0];
      fangleVectors(angles, forward, right, up);
      lp = [fdot(lp, forward), -fdot(lp, right), fdot(lp, up)];
    }
    let leaf: CLeaf;
    if (model === 0) {
      let num = this.headnode;
      while (num >= 0) {
        const node = this.nodes[num];
        const d = node.plane.type < 3 ? f(lp[node.plane.type] - node.plane.dist) : f(fdot(node.plane.normal, lp) - node.plane.dist);
        num = d < 0 ? node.children[1] : node.children[0];
      }
      leaf = this.leafs[-1 - num];
    } else {
      const cmod = this.models[model];
      if (!cmod) return 0;
      leaf = cmod.leaf;
    }
    let contents = 0;
    for (const k of leaf.brushes) {
      const b = this.brushes[k];
      // see if the point is in the brush
      let inside = true;
      for (const s of b.sides) {
        if (fdot(lp, s.plane.normal) > s.plane.dist) {
          inside = false;
          break;
        }
      }
      if (inside) contents |= b.contents;
    }
    return contents;
  }
}

const ROT_SCALE = f(f(M_PI_F * 2) / 360);

/** cm_trace.c CreateRotationMatrix: rows forward, left (-right), up of the angles. */
export function createRotationMatrix(angles: ArrayLike<number>): Vec3[] {
  let angle = f(angles[1] * ROT_SCALE);
  const sy = f(Math.sin(angle));
  const cy = f(Math.cos(angle));
  angle = f(angles[0] * ROT_SCALE);
  const sp = f(Math.sin(angle));
  const cp = f(Math.cos(angle));
  angle = f(angles[2] * ROT_SCALE);
  const sr = f(Math.sin(angle));
  const cr = f(Math.cos(angle));
  return [
    [f(cp * cy), f(cp * sy), -sp],
    [f(f(f(sr * sp) * cy) + f(cr * -sy)), f(f(f(sr * sp) * sy) + f(cr * cy)), f(sr * cp)],
    [f(f(f(cr * sp) * cy) + f(-sr * -sy)), f(f(f(cr * sp) * sy) + f(-sr * cy)), f(cr * cp)],
  ];
}

/** RotatePoint: the point expressed in the matrix rows. */
export function rotatePoint(p: ArrayLike<number>, matrix: ArrayLike<number>[]): Vec3 {
  return [fdot(matrix[0], p), fdot(matrix[1], p), fdot(matrix[2], p)];
}

/** BoxOnPlaneSide: 1 = front, 2 = back, 3 = both. */
function boxOnPlaneSide(mins: Vec3, maxs: Vec3, p: CPlane): number {
  if (p.type < 3) {
    if (p.dist <= mins[p.type]) return 1;
    if (p.dist >= maxs[p.type]) return 2;
    return 3;
  }
  let dist1 = 0;
  let dist2 = 0;
  for (let i = 0; i < 3; i++) {
    const n = p.normal[i];
    if (n < 0) {
      dist1 += n * mins[i];
      dist2 += n * maxs[i];
    } else {
      dist1 += n * maxs[i];
      dist2 += n * mins[i];
    }
  }
  let sides = 0;
  if (dist1 >= p.dist) sides = 1;
  if (dist2 < p.dist) sides |= 2;
  return sides;
}
